import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import UpgradeModal from "@/components/UpgradeModal";
import { useAuth } from "@/contexts/AuthContext";

function EditGroupDialog({ open, group, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && group) {
      setName(group.name || "");
      setDesc(group.description || "");
    }
  }, [open, group]);

  if (!open || !group) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Give the group a name."); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/groups/${group.id}`, { name, description: desc });
      onSaved(data);
      toast.success("Group updated");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="group-edit-dialog">
      <div className="jj-card w-full max-w-lg p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy"><X className="h-5 w-5" /></button>
        <div className="overline text-burgundy">Edit prayer group</div>
        <h2 className="font-serif text-2xl text-jjblue mt-2 leading-tight">Update the details</h2>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input className="input-jj" placeholder="Group name" value={name} onChange={(e) => setName(e.target.value)} data-testid="group-edit-name" />
          <textarea rows={4} maxLength={500} className="input-jj" placeholder="What is this group about?" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="group-edit-desc" />
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="group-edit-submit">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Groups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [upgrade, setUpgrade] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/groups");
    setGroups(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/groups", { name, description: desc });
      setName(""); setDesc(""); setOpen(false);
      toast.success("Group created");
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403 && typeof detail === "string") {
        setOpen(false);
        setUpgrade({ tier: "Believer", reason: detail });
      } else {
        toast.error("Could not create group");
      }
    }
    finally { setBusy(false); }
  };

  const join = async (g) => {
    await api.post(`/groups/${g.id}/${g.is_member ? "leave" : "join"}`);
    load();
  };

  const removeGroup = async (g) => {
    if (!window.confirm(`Delete the group "${g.name}"? This will also remove its prayer requests.`)) return;
    try {
      await api.delete(`/groups/${g.id}`);
      setGroups((arr) => arr.filter((x) => x.id !== g.id));
      toast.success("Group deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-burgundy">Prayer Groups</div>
          <h1 className="font-serif text-4xl text-jjblue mt-2">Find your circle of intercession</h1>
          <p className="mt-2 text-[var(--jj-text-secondary)]">Small groups carry deep weight. Join one, or start your own.</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setOpen(!open)} data-testid="new-group-btn">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> New group
        </button>
      </div>

      {open && (
        <form onSubmit={create} className="jj-card p-6 mt-6 max-w-xl" data-testid="new-group-form">
          <label className="text-sm text-jjblue/80">Name</label>
          <input className="input-jj mt-1" required value={name} onChange={(e) => setName(e.target.value)} data-testid="group-name" />
          <label className="text-sm text-jjblue/80 mt-4 block">Description</label>
          <textarea rows={3} className="input-jj mt-1" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="group-desc" />
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy} className="btn-primary !py-2 !px-4 text-sm" data-testid="group-create">{busy ? "Creating…" : "Create"}</button>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {groups.map((g) => {
          const canManage = user && (user.role === "admin" || g.owner_id === user.id);
          return (
            <div key={g.id} className="jj-card p-6 flex flex-col" data-testid={`group-card-${g.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="overline text-burgundy">Prayer Group</div>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditing(g)} className="p-1 text-[var(--jj-muted)] hover:text-burgundy" title="Edit" data-testid={`group-edit-${g.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => removeGroup(g)} className="p-1 text-[var(--jj-muted)] hover:text-burgundy" title="Delete" data-testid={`group-delete-${g.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <h3 className="font-serif text-2xl text-jjblue mt-2">{g.name}</h3>
              <p className="mt-3 text-sm text-[var(--jj-text-secondary)] line-clamp-3 min-h-[3.5rem]">{g.description || "A quiet place to pray together."}</p>
              <div className="mt-5 flex items-center justify-between">
                <span className="text-xs text-[var(--jj-muted)] flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {g.member_count} members</span>
                <div className="flex gap-2">
                  <button onClick={() => join(g)} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`group-join-${g.id}`}>
                    {g.is_member ? "Leave" : "Join"}
                  </button>
                  <Link to={`/groups/${g.id}`} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`group-open-${g.id}`}>Open</Link>
                </div>
              </div>
            </div>
          );
        })}
        {groups.length === 0 && <div className="text-[var(--jj-muted)] col-span-full text-center py-10">No groups yet. Be the first to start one.</div>}
      </div>

      <UpgradeModal
        open={!!upgrade}
        onClose={() => setUpgrade(null)}
        tier={upgrade?.tier}
        reason={upgrade?.reason}
      />
      <EditGroupDialog
        open={!!editing}
        group={editing}
        onClose={() => setEditing(null)}
        onSaved={(updated) => setGroups((arr) => arr.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))}
      />
    </div>
  );
}
